import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import useHomeScreenApp from '../../utils/useHomeScreenApp';
import { db } from '../../config/config';
import {
  Box,
  Container,
  Typography,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { filterPanel, posterGrid } from '../../utils/surfaces';
import AddWatchlistModal from './components/AddWatchlistModal';
import EditWatchlistModal from './components/EditWatchlistModal';
import ConfirmDeleteModal from './components/ConfirmDeleteWatchlistModal';
import WatchlistCard from './components/WatchlistCard';

export type WatchlistStatus = 'toWatch' | 'watched';

export interface WatchlistMovie {
  id: string;
  title: string;
  year: number;
  image: string;
  status: WatchlistStatus;
  watchCount: number;
  lastWatched: Timestamp | null;
  dateAdded: Timestamp;
  // Rating: at most one of these is true; neither = unrated.
  favourite?: boolean;
  disliked?: boolean;
  // Position on the To Watch tab (lowest first). Unused once watched.
  order?: number;
}

export type Rating = 'favourite' | 'disliked' | 'none';

export const getRating = (m: WatchlistMovie): Rating =>
  m.favourite ? 'favourite' : m.disliked ? 'disliked' : 'none';

const NEXT_RATING: Record<Rating, Rating> = {
  none: 'favourite',
  favourite: 'disliked',
  disliked: 'none',
};

type RatingFilter = 'all' | 'favourite' | 'disliked';

type ToWatchSort = 'custom' | 'date-desc' | 'date-asc';
type WatchedSort = 'date-desc' | 'date-asc';

const COLLECTION_NAME = 'Watchlist';

const addedMillis = (m: WatchlistMovie) => m.dateAdded?.toMillis?.() ?? 0;

// Both lists take title and year from TMDB, so a loose title match plus the
// year finds movies already owned on disc.
const normaliseTitle = (title: string) =>
  title
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

type OwnedFormats = Map<string, { year: number; formats: string[] }[]>;

const findOwnedFormats = (owned: OwnedFormats, movie: WatchlistMovie) => {
  const matches = (owned.get(normaliseTitle(movie.title)) ?? []).filter(
    (entry) => !entry.year || !movie.year || entry.year === movie.year
  );
  const formats = Array.from(new Set(matches.flatMap((entry) => entry.formats)));
  return formats.length ? formats : undefined;
};

const Watchlist = () => {
  const [movies, setMovies] = useState<WatchlistMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<WatchlistStatus>('toWatch');
  const [toWatchSort, setToWatchSort] = useState<ToWatchSort>('custom');
  const [watchedSort, setWatchedSort] = useState<WatchedSort>('date-desc');
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');

  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [owned, setOwned] = useState<OwnedFormats>(new Map());

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedMovie, setSelectedMovie] = useState<WatchlistMovie | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /* ─────────────────────────────
   * Track auth state — anonymous visitors are read-only
   * ───────────────────────────── */
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsReadOnly(!user);
    });
    return () => unsub();
  }, []);

  // Its own manifest, so Add to Home Screen saves it as "Watchlist"
  useHomeScreenApp('/manifest-watchlist.json', 'Watchlist');

  /* ─────────────────────────────
   * Fetch movies
   * ───────────────────────────── */
  useEffect(() => {
    const fetchMovies = async () => {
      try {
        const ref = collection(db, COLLECTION_NAME);
        const snapshot = await getDocs(ref);
        const list = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as WatchlistMovie[];
        setMovies(list);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMovies();
  }, []);

  /* ─────────────────────────────
   * Physical collection, for the Owned badge. It's private, so only the
   * signed-in owner loads it.
   * ───────────────────────────── */
  useEffect(() => {
    if (isReadOnly) {
      setOwned(new Map());
      return;
    }

    const fetchOwned = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'PhysicalMovieCollection'));
        const map: OwnedFormats = new Map();
        snapshot.docs.forEach((d) => {
          const { title, year, type, wishlist } = d.data();
          if (wishlist) return;
          if (!title) return;
          const key = normaliseTitle(title);
          map.set(key, [...(map.get(key) ?? []), { year: year ?? 0, formats: [type] }]);
        });
        setOwned(map);
      } catch (err) {
        console.error('Failed to load the physical movie collection', err);
      }
    };

    fetchOwned();
  }, [isReadOnly]);

  /* ─────────────────────────────
   * One-off: give To Watch movies an `order`, newest added first (the old
   * default view). Only the owner can write, so it waits for sign-in.
   * ───────────────────────────── */
  const orderBackfillStarted = useRef(false);
  useEffect(() => {
    if (loading || isReadOnly || orderBackfillStarted.current) return;

    const toWatch = movies.filter((m) => m.status === 'toWatch');
    if (!toWatch.some((m) => m.order === undefined)) return;
    orderBackfillStarted.current = true;

    const updates = [...toWatch]
      .sort((a, b) => addedMillis(b) - addedMillis(a))
      .map((movie, order) => ({ movie, order }));

    const backfill = async () => {
      try {
        const batch = writeBatch(db);
        updates.forEach(({ movie, order }) => {
          batch.update(doc(db, COLLECTION_NAME, movie.id), { order });
        });
        await batch.commit();
        setMovies((prev) =>
          prev.map((m) => {
            const update = updates.find((u) => u.movie.id === m.id);
            return update ? { ...m, order: update.order } : m;
          })
        );
      } catch (err) {
        console.error('To Watch order backfill failed', err);
      }
    };

    backfill();
  }, [loading, isReadOnly, movies]);

  /* ─────────────────────────────
   * Reset rating filter when leaving Watched tab
   * ───────────────────────────── */
  useEffect(() => {
    if (tab !== 'watched') setRatingFilter('all');
  }, [tab]);

  /* ─────────────────────────────
   * Filter (by tab, rating) + sort
   *   - To Watch → My order (`order`, newest added if missing) or dateAdded
   *   - Watched  → lastWatched
   * ───────────────────────────── */
  const visibleMovies = useMemo(() => {
    let filtered = movies.filter((m) => m.status === tab);

    if (tab === 'watched' && ratingFilter !== 'all') {
      filtered = filtered.filter((m) => getRating(m) === ratingFilter);
    }

    const sorted = [...filtered];

    if (tab === 'toWatch' && toWatchSort === 'custom') {
      return sorted.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        return addedMillis(b) - addedMillis(a);
      });
    }

    const sortOption = tab === 'watched' ? watchedSort : toWatchSort;
    const getTime = (m: WatchlistMovie) =>
      tab === 'watched' ? m.lastWatched?.toMillis?.() ?? 0 : addedMillis(m);

    return sorted.sort((a, b) => {
      const aTime = getTime(a);
      const bTime = getTime(b);
      return sortOption === 'date-desc' ? bTime - aTime : aTime - bTime;
    });
  }, [movies, tab, toWatchSort, watchedSort, ratingFilter]);

  const canDrag = tab === 'toWatch' && toWatchSort === 'custom' && !isReadOnly;

  // New and returning To Watch movies go to the top of My order.
  const topOrder = () => {
    const orders = movies
      .filter((m) => m.status === 'toWatch' && m.order !== undefined)
      .map((m) => m.order as number);
    return orders.length ? Math.min(...orders) - 1 : 0;
  };

  /* ─────────────────────────────
   * CRUD
   * ───────────────────────────── */
  const handleAddMovie = async (newMovie: Omit<WatchlistMovie, 'id'>) => {
    const existing = movies.find(
      (m) =>
        m.title.toLowerCase() === newMovie.title.toLowerCase() &&
        m.year === newMovie.year
    );

    if (existing) {
      if (existing.status === 'toWatch') {
        toast.error(`"${newMovie.title}" is already in your watchlist`);
        return;
      }

      try {
        const ref = doc(db, COLLECTION_NAME, existing.id);
        const updates = { status: 'toWatch' as const, order: topOrder() };
        await updateDoc(ref, updates);
        setMovies((prev) =>
          prev.map((m) => (m.id === existing.id ? { ...m, ...updates } : m))
        );
        toast.success(`"${existing.title}" moved back to To Watch`);
      } catch {
        toast.error('Failed to move movie.');
      }
      return;
    }

    try {
      const ref = collection(db, COLLECTION_NAME);
      const entry = { ...newMovie, order: topOrder() };
      const docRef = await addDoc(ref, entry);
      setMovies([...movies, { ...entry, id: docRef.id }]);
      toast.success(`Added "${newMovie.title}"`);
    } catch {
      toast.error('Failed to add movie.');
    }
  };

  const handleMarkAsWatched = async (movie: WatchlistMovie) => {
    try {
      const ref = doc(db, COLLECTION_NAME, movie.id);
      const updates = {
        status: 'watched' as const,
        watchCount: movie.watchCount + 1,
        lastWatched: Timestamp.now(),
      };
      await updateDoc(ref, updates);
      setMovies((prev) =>
        prev.map((m) => (m.id === movie.id ? { ...m, ...updates } : m))
      );
      toast.success(`Marked "${movie.title}" as watched`);
    } catch {
      toast.error('Failed to update.');
    }
  };

  const handleMoveToWatch = async (movie: WatchlistMovie) => {
    try {
      const ref = doc(db, COLLECTION_NAME, movie.id);
      const updates = { status: 'toWatch' as const, order: topOrder() };
      await updateDoc(ref, updates);
      setMovies((prev) =>
        prev.map((m) => (m.id === movie.id ? { ...m, ...updates } : m))
      );
      toast.success(`Moved "${movie.title}" back to To Watch`);
    } catch {
      toast.error('Failed to update.');
    }
  };

  // Cycles unrated → favourite → disliked → unrated
  const handleCycleRating = async (movie: WatchlistMovie) => {
    const next = NEXT_RATING[getRating(movie)];
    const updates = {
      favourite: next === 'favourite',
      disliked: next === 'disliked',
    };
    try {
      const ref = doc(db, COLLECTION_NAME, movie.id);
      await updateDoc(ref, updates);
      setMovies((prev) =>
        prev.map((m) => (m.id === movie.id ? { ...m, ...updates } : m))
      );
    } catch {
      toast.error('Failed to update.');
    }
  };

  const handleOpenImdb = async (movie: WatchlistMovie) => {
    const searchFallbackUrl = `https://www.imdb.com/find/?q=${encodeURIComponent(
      movie.title
    )}&s=tt`;

    try {
      const searchRes = await axios.get(
        'https://api.themoviedb.org/3/search/movie',
        {
          params: {
            api_key: process.env.REACT_APP_TMDB_API_KEY,
            query: movie.title,
            year: movie.year || undefined,
          },
        }
      );

      const tmdbId = searchRes.data?.results?.[0]?.id;
      if (!tmdbId) {
        window.location.href = searchFallbackUrl;
        return;
      }

      const externalRes = await axios.get(
        `https://api.themoviedb.org/3/movie/${tmdbId}/external_ids`,
        { params: { api_key: process.env.REACT_APP_TMDB_API_KEY } }
      );

      const imdbId = externalRes.data?.imdb_id;
      if (imdbId) {
        window.location.href = `https://www.imdb.com/title/${imdbId}/`;
      } else {
        window.location.href = searchFallbackUrl;
      }
    } catch {
      window.location.href = searchFallbackUrl;
    }
  };

  const handleEditMovie = async (
    id: string,
    updates: Partial<WatchlistMovie>
  ) => {
    try {
      const ref = doc(db, COLLECTION_NAME, id);
      await updateDoc(ref, updates);
      setMovies((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
      );
      toast.success('Updated');
    } catch {
      toast.error('Failed to update.');
    }
  };

  const handleRemoveMovie = async () => {
    if (!selectedMovie) return;

    const shouldMoveBackToWatched =
      selectedMovie.status === 'toWatch' && selectedMovie.watchCount > 0;

    try {
      const ref = doc(db, COLLECTION_NAME, selectedMovie.id);

      if (shouldMoveBackToWatched) {
        await updateDoc(ref, { status: 'watched' });
        setMovies((prev) =>
          prev.map((m) =>
            m.id === selectedMovie.id
              ? { ...m, status: 'watched' as const }
              : m
          )
        );
        toast.success(`"${selectedMovie.title}" moved back to Watched`);
      } else {
        await deleteDoc(ref);
        setMovies((prev) => prev.filter((m) => m.id !== selectedMovie.id));
        toast.success(`Removed "${selectedMovie.title}"`);
      }

      setShowDeleteModal(false);
    } catch {
      toast.error('Failed to remove.');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visibleMovies.findIndex((m) => m.id === active.id);
    const newIndex = visibleMovies.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const updates = arrayMove(visibleMovies, oldIndex, newIndex)
      .map((movie, order) => ({ movie, order }))
      .filter(({ movie, order }) => movie.order !== order);

    if (updates.length === 0) return;

    const previous = movies;
    setMovies((prev) =>
      prev.map((m) => {
        const update = updates.find((u) => u.movie.id === m.id);
        return update ? { ...m, order: update.order } : m;
      })
    );

    try {
      const batch = writeBatch(db);
      updates.forEach(({ movie, order }) => {
        batch.update(doc(db, COLLECTION_NAME, movie.id), { order });
      });
      await batch.commit();
    } catch (err) {
      console.error(err);
      setMovies(previous);
      toast.error('Failed to save the new order');
    }
  };

  /* ─────────────────────────────
   * Menu
   * ───────────────────────────── */
  const handleMenuClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    movie: WatchlistMovie
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedMovie(movie);
  };

  const handleMenuClose = () => setAnchorEl(null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      {/* Same cap as TV Watchlist so huge screens don't stretch the grid */}
      <Container maxWidth="xl" sx={{ p: 3, px: { lg: 5 } }}>
        <ToastContainer />

        <StandaloneBackButton />
        <PageHeading>Watchlist</PageHeading>

        {/* Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v: WatchlistStatus) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="To Watch" value="toWatch" />
            <Tab label="Watched" value="watched" />
          </Tabs>
        </Box>

        {/* Filter bar — always one row */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            mb: 3,
            ...filterPanel(theme),
          })}
        >
          <FormControl
            size="small"
            sx={{ flexGrow: 1, minWidth: 0, maxWidth: 220 }}
          >
            <InputLabel>Sort By</InputLabel>
            <Select
              value={tab === 'watched' ? watchedSort : toWatchSort}
              label="Sort By"
              onChange={(e) =>
                tab === 'watched'
                  ? setWatchedSort(e.target.value as WatchedSort)
                  : setToWatchSort(e.target.value as ToWatchSort)
              }
              MenuProps={{
                PaperProps: { sx: { backgroundImage: 'none' } },
                transitionDuration: 0,
              }}
            >
              {tab === 'toWatch' && (
                <MenuItem value="custom">My order</MenuItem>
              )}
              <MenuItem value="date-desc">
                {tab === 'watched' ? 'Most recent' : 'Newest first'}
              </MenuItem>
              <MenuItem value="date-asc">
                {tab === 'watched' ? 'Least recent' : 'Oldest first'}
              </MenuItem>
            </Select>
          </FormControl>

          {tab === 'watched' && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ratingFilter}
              onChange={(_, value: RatingFilter | null) => {
                // null when the active button is clicked again — keep it.
                if (value) setRatingFilter(value);
              }}
              aria-label="Filter by rating"
              sx={{
                flexShrink: 0,
                '& .MuiToggleButton-root': {
                  px: 1.25,
                  py: 0.5,
                  height: 36,
                  fontSize: 12,
                  fontWeight: 800,
                  borderColor: 'divider',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'rgba(255,47,146,0.18)',
                  borderColor: 'rgba(255,47,146,0.55)',
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="favourite" aria-label="Favourites">
                <Tooltip title="Favourites">
                  <StarIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="disliked" aria-label="Disliked">
                <Tooltip title="Disliked">
                  <ThumbDownIcon sx={{ fontSize: 16 }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {!isReadOnly && (
            <Button
              variant="contained"
              sx={{ ml: 'auto' }}
              onClick={() => setShowAddModal(true)}
            >
              Add Movie
            </Button>
          )}
        </Box>

        <Typography align="right" sx={{ mb: 2 }}>
          Showing {visibleMovies.length}{' '}
          {visibleMovies.length === 1 ? 'movie' : 'movies'}
        </Typography>

        {/* Grid */}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : visibleMovies.length === 0 ? (
          <Typography align="center" color="text.secondary" sx={{ mt: 6 }}>
            {tab === 'toWatch'
              ? 'No movies on your watchlist yet.'
              : ratingFilter === 'favourite'
              ? 'No favourite movies yet.'
              : ratingFilter === 'disliked'
              ? 'No disliked movies yet.'
              : 'No movies watched yet.'}
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleMovies.map((movie) => movie.id)}
              strategy={rectSortingStrategy}
            >
              <Box sx={posterGrid}>
                {visibleMovies.map((movie) => (
                  <WatchlistCard
                    key={movie.id}
                    movie={movie}
                    isToWatchTab={tab === 'toWatch'}
                    isReadOnly={isReadOnly}
                    draggable={canDrag}
                    ownedFormats={
                      tab === 'toWatch' ? findOwnedFormats(owned, movie) : undefined
                    }
                    onMenuClick={handleMenuClick}
                    onMarkAsWatched={handleMarkAsWatched}
                    onCycleRating={handleCycleRating}
                    onOpenImdb={handleOpenImdb}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transitionDuration={0}
          PaperProps={{ sx: { backgroundImage: 'none' } }}
        >
          <MenuItem
            onClick={() => {
              setShowEditModal(true);
              handleMenuClose();
            }}
          >
            Edit
          </MenuItem>

          {selectedMovie?.status === 'watched' && (
            <MenuItem
              onClick={() => {
                if (selectedMovie) handleMoveToWatch(selectedMovie);
                handleMenuClose();
              }}
            >
              Move to To Watch
            </MenuItem>
          )}

          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setShowDeleteModal(true);
              handleMenuClose();
            }}
          >
            Remove
          </MenuItem>
        </Menu>

        {/* Modals */}
        <AddWatchlistModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onAddMovie={handleAddMovie}
        />

        {selectedMovie && (
          <EditWatchlistModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            movie={selectedMovie}
            onEditMovie={handleEditMovie}
          />
        )}

        {selectedMovie && (
          <ConfirmDeleteModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            onConfirm={handleRemoveMovie}
            movieTitle={selectedMovie.title}
          />
        )}
      </Container>
    </Box>
  );
};

export default Watchlist;